import {
  CURRENT_STAGE,
  canAnywhere,
  MODULES,
  NAV_GROUP_TITLES,
  type NavGroup,
  type PortalModule,
} from '@epc/contracts';
import * as RD from '@radix-ui/react-dialog';
import * as DM from '@radix-ui/react-dropdown-menu';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  Bell,
  Building2,
  CircleHelp,
  Compass,
  KeyRound,
  ListChecks,
  LogOut,
  Menu,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { AnnouncementsBanner } from '../components/Announcements';
import { DemoBanner } from '../components/Demo';
import { LicenseBanner, SuspendedScreen } from '../components/License';
import { PasskeyOffer } from '../components/Passkeys';
import { PlatformEntryBanner } from '../components/PlatformEntry';
import { UpdateBanner } from '../components/UpdateBanner';
import { ViewAsBanner } from '../components/ViewAs';
import { api } from '../lib/api';
import { initials, useMe } from '../lib/session';
import { supportMeQuery, supportUi } from '../lib/support';
import { useHasUnseenReleases } from '../lib/whatsnew';
import { inboxQuery } from '../pages/InboxPage';
import { SupportDialogs } from '../pages/support/SupportDialogs';
import { toast } from '../ui/feedback';
import { AiPanel } from './AiPanel';
import { CommandPalette } from './CommandPalette';
import { buildTrail, type Crumb, CrumbsProvider, LocationBar } from './crumbs';
import { HelpPanel } from './HelpPanel';
import { HubPanel } from './HubPanel';
import { Logo } from './Logo';
import { ReportButton, ReportPanel } from './ReportPanel';
import { Tour } from './Tour';

function isActive(pathname: string, m: PortalModule) {
  if (m.path === '/') return pathname === '/';
  return pathname === m.path || pathname.startsWith(`${m.path}/`);
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const me = useMe();
  const inbox = useQuery({ ...inboxQuery, enabled: canAnywhere(me.access, 'project.view') });
  const inboxCount = inbox.data?.length ?? 0;
  const inboxOverdue = inbox.data?.some((i) => i.overdue) ?? false;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const unseen = useHasUnseenReleases();
  const supplierCabinet = Boolean(me.tenant?.counterpartyId && !me.tenant.staff);
  const groups = (Object.keys(NAV_GROUP_TITLES) as NavGroup[]).map((g) => ({
    group: g,
    items: MODULES.filter((m) => m.group === g && canAnywhere(me.access, m.permission)).map((m) =>
      // Внешний поставщик: «Закупки» ведут в кабинет ответа на КП (ВК-06), а не во внутренний реестр
      supplierCabinet && m.code === 'procurement'
        ? { ...m, title: 'Кабинет поставщика', path: '/suppliers/cabinet' }
        : m,
    ),
  }));

  return (
    <nav aria-label="Разделы портала" className="flex flex-col gap-5 px-3 pb-6">
      {groups
        .filter((g) => g.items.length)
        .map(({ group, items }) => (
          <div key={group}>
            <div className="px-3 pb-1.5 text-[12px] font-medium text-nav-muted">
              {NAV_GROUP_TITLES[group]}
            </div>
            <ul className="flex flex-col gap-px">
              {items.map((m) => {
                const active = isActive(pathname, m);
                // Раздел следующего этапа, который уже выложен другим потоком, помечать не нужно
                const later = m.stage > CURRENT_STAGE && !m.releasedAt;
                if (later && me.tenant?.isDemo) return null;
                return (
                  <li key={m.code}>
                    <Link
                      to={m.path}
                      onClick={onNavigate}
                      aria-current={active ? 'page' : undefined}
                      className={clsx(
                        'group flex h-9 items-center gap-3 rounded-md px-3 text-[14px] transition-colors',
                        active
                          ? 'bg-nav-active text-white'
                          : 'text-nav-ink hover:bg-white/5 hover:text-white',
                      )}
                    >
                      <span
                        aria-hidden
                        className={clsx(
                          'size-1.5 shrink-0 rounded-full',
                          active ? 'bg-accent' : 'bg-nav-muted/50 group-hover:bg-nav-ink',
                        )}
                      />
                      <span className="flex-1 truncate">{m.title}</span>
                      {m.code === 'inbox' && inboxCount ? (
                        <span
                          className={clsx(
                            'num min-w-6 rounded-sm px-1.5 text-center text-[12px] leading-5 font-semibold',
                            inboxOverdue ? 'bg-accent text-on-accent' : 'bg-white/10 text-white',
                          )}
                          title={inboxOverdue ? 'Есть просроченные дела' : 'Дел ждёт вас'}
                        >
                          {inboxCount}
                        </span>
                      ) : null}
                      {m.code === 'whatsnew' && unseen ? (
                        <span className="size-2 rounded-full bg-accent" title="Есть новые изменения" />
                      ) : null}
                      {later ? (
                        <span className="text-[11px] text-nav-muted" title={`Появится на этапе ${m.stage}`}>
                          эт. {m.stage}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      {/* Кабинет портала (ТЗ 6.29–6.30, ЛЦ-09) — команде портала: владельцу и администраторам; это не право компании */}
      {(me.user.isPlatformAdmin || me.user.platformRole) && !me.viewAs ? (
        <div>
          <div className="px-3 pb-1.5 text-[12px] font-medium text-nav-muted">Портал</div>
          <ul className="flex flex-col gap-px">
            <li>
              <Link
                to="/platform"
                onClick={onNavigate}
                aria-current={pathname.startsWith('/platform') ? 'page' : undefined}
                className={clsx(
                  'group flex h-9 items-center gap-3 rounded-md px-3 text-[14px] transition-colors',
                  pathname.startsWith('/platform')
                    ? 'bg-nav-active text-white'
                    : 'text-nav-ink hover:bg-white/5 hover:text-white',
                )}
              >
                <ShieldCheck
                  className="size-4 shrink-0 text-nav-muted group-hover:text-nav-ink"
                  aria-hidden
                />
                <span className="flex-1 truncate">
                  {me.user.isPlatformAdmin ? 'Владелец портала' : 'Администрирование портала'}
                </span>
              </Link>
            </li>
          </ul>
        </div>
      ) : null}
    </nav>
  );
}

function SidebarBrand() {
  const me = useMe();
  return (
    <div className="flex flex-col gap-2 px-6 pt-6 pb-6">
      <Logo height={23} className="text-white" />
      <div className="min-w-0">
        <div className="truncate pl-[2px] text-[12px] leading-4 text-nav-muted">
          {me.tenant?.name ?? 'Нет компании'}
        </div>
      </div>
    </div>
  );
}

function UserMenu() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();

  async function logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    qc.clear();
    navigate({ to: '/login' });
  }

  async function switchTenant(id: string) {
    const next = await api('/auth/tenant', { body: { tenantId: id } });
    qc.clear();
    qc.setQueryData(['me'], next);
    navigate({ to: '/' });
    toast('Компания переключена');
  }

  return (
    <DM.Root>
      <DM.Trigger
        className="flex size-11 items-center justify-center rounded-full bg-tile text-[13px] font-semibold text-ink hover:bg-line md:size-9"
        aria-label="Меню пользователя"
      >
        {initials(me.user.fullName)}
      </DM.Trigger>
      <DM.Portal>
        <DM.Content
          align="end"
          sideOffset={8}
          className="z-50 w-72 rounded-md border border-line bg-surface p-1.5 text-[14px] shadow-md"
        >
          <div className="px-3 py-2">
            <div className="font-semibold text-ink">{me.user.fullName}</div>
            <div className="truncate text-[13px] text-ink-2">{me.user.email}</div>
            {me.roles.length ? (
              <div className="mt-1 text-[12px] text-ink-2">
                {me.roles.map((r) => (r.scopeName ? `${r.name} (${r.scopeName})` : r.name)).join(', ')}
              </div>
            ) : null}
          </div>
          <DM.Separator className="my-1 h-px bg-line" />
          {me.tenants.length > 1
            ? me.tenants.map((t) => (
                <DM.Item
                  key={t.id}
                  onSelect={() => void switchTenant(t.id)}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-surface-2"
                >
                  <Building2 className="size-4 text-ink-2" />
                  <span className="flex-1 truncate">{t.name}</span>
                  {t.id === me.tenant?.id ? (
                    <span className="size-1.5 rounded-full bg-accent">
                      <span className="sr-only">текущая</span>
                    </span>
                  ) : null}
                </DM.Item>
              ))
            : null}
          <DM.Item
            onSelect={() => navigate({ to: '/profile' })}
            className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-surface-2"
          >
            <KeyRound className="size-4 text-ink-2" />
            Профиль и пароль
          </DM.Item>
          <DM.Item
            onSelect={() => supportUi.openTour()}
            className="flex min-h-11 cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-surface-2 md:min-h-0"
          >
            <Compass className="size-4 text-ink-2" />
            Экскурсия по порталу
          </DM.Item>
          <DM.Item
            onSelect={() => navigate({ to: '/start' })}
            className="flex min-h-11 cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-surface-2 md:min-h-0"
          >
            <ListChecks className="size-4 text-ink-2" />С чего начать
          </DM.Item>
          <DM.Item
            onSelect={() => void logout()}
            className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-surface-2"
          >
            <LogOut className="size-4 text-ink-2" />
            Выйти
          </DM.Item>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

export function AppShell() {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const [ai, setAi] = useState(false);
  const [hub, setHub] = useState(false);
  const inbox = useQuery({
    ...inboxQuery,
    enabled: canAnywhere(me.access, 'project.view'),
  });
  const inboxCount = inbox.data?.length ?? 0;
  const inboxOverdue = inbox.data?.some((i) => i.overdue) ?? false;
  // «Где вы находитесь»: раздел по адресу, дальше — то, что сообщила страница (проект, документ)
  const [tail, setTail] = useState<Crumb[] | null>(null);
  const trail = buildTrail(pathname, tail);
  const current = trail[trail.length - 1]?.label ?? '';

  // Название вкладки — текущая страница: история браузера и закладки показывают, где вы были
  useEffect(() => {
    if (current && current !== '…') document.title = `${current} — EPC Pilot AI`;
  }, [current]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Первый вход: экскурсия по разделам роли открывается сама, один раз (БЗ-03)
  const help = useQuery({ ...supportMeQuery, enabled: !!me.tenant?.staff });
  const tourPending = help.data ? help.data.tourDoneAt === null : false;
  useEffect(() => {
    if (tourPending && !me.tenant?.isDemo) supportUi.openTour();
  }, [tourPending, me.tenant?.isDemo]);

  // Доступ компании приостановлен владельцем портала: API закрыт, вместо разделов — объяснение и выход
  if (me.license?.state === 'suspended') return <SuspendedScreen />;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[264px_1fr] print:block">
      <aside className="sticky top-0 hidden h-dvh flex-col overflow-y-auto bg-nav-bg lg:flex print:hidden">
        <SidebarBrand />
        <NavList />
      </aside>

      <RD.Root open={drawer} onOpenChange={setDrawer}>
        <RD.Portal>
          <RD.Overlay className="fixed inset-0 z-40 bg-ink/50 lg:hidden" />
          <RD.Content className="fixed inset-y-0 left-0 z-50 flex w-[288px] flex-col overflow-y-auto bg-nav-bg lg:hidden">
            <RD.Title className="sr-only">Меню</RD.Title>
            <RD.Description className="sr-only">Разделы портала</RD.Description>
            <div className="flex items-center justify-between pr-3">
              <SidebarBrand />
              <RD.Close
                className="inline-flex size-11 items-center justify-center rounded-md text-nav-ink hover:text-white"
                aria-label="Закрыть меню"
              >
                <X className="size-5" />
              </RD.Close>
            </div>
            <NavList onNavigate={() => setDrawer(false)} />
          </RD.Content>
        </RD.Portal>
      </RD.Root>

      <div className="flex min-w-0 flex-col">
        {/* Шапка, плашка обновления и строка «где вы находитесь» прилипают к верху вместе */}
        <div className="sticky top-0 z-30 print:hidden">
          <UpdateBanner />
          <LicenseBanner />
          <DemoBanner />
          <ViewAsBanner />
          <PlatformEntryBanner />
          <AnnouncementsBanner />
          <header className="flex h-16 items-center gap-3 border-b border-line bg-surface/95 px-4 backdrop-blur md:px-8">
            <button
              type="button"
              onClick={() => setDrawer(true)}
              className="-ml-2 inline-flex size-11 items-center justify-center rounded-md text-ink-2 hover:bg-tile lg:hidden"
              aria-label="Открыть меню"
            >
              <Menu className="size-5" />
            </button>
            <Link
              to="/"
              aria-label="EPC Pilot AI — на главную"
              className="mr-auto flex h-11 items-center md:hidden"
            >
              <Logo height={20} className="text-ink" />
            </Link>
            <LocationBar trail={trail} className="hidden flex-1 md:flex" />
            <button
              type="button"
              onClick={() => setPalette(true)}
              className="hidden h-10 w-[min(420px,36vw)] items-center gap-2 rounded-md border border-line-strong bg-surface-2 px-3 text-left text-[14px] text-ink-3 hover:border-ink-3 md:flex"
            >
              <Search className="size-4" aria-hidden />
              <span className="flex-1 truncate">Поиск: раздел, заявка, договор, чертёж</span>
              <kbd className="rounded-sm border border-line bg-surface px-1.5 text-[11px] text-ink-2">
                Ctrl K
              </kbd>
            </button>
            <button
              type="button"
              onClick={() => setPalette(true)}
              className="inline-flex size-11 items-center justify-center rounded-md text-ink-2 hover:bg-tile md:hidden"
              aria-label="Поиск"
            >
              <Search className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => setHub(true)}
              className="relative inline-flex size-11 items-center justify-center rounded-md text-ink-2 hover:bg-tile hover:text-ink md:size-10"
              aria-label={
                inboxCount
                  ? `Сообщения и задачи, дел ${inboxCount}${inboxOverdue ? ', есть просроченные' : ''}`
                  : 'Сообщения и задачи'
              }
              title="Сообщения и задачи"
            >
              <Bell className="size-5" />
              {inboxCount ? (
                <span
                  className={clsx(
                    'num absolute top-1 right-1 min-w-4 rounded-sm px-1 text-center text-[10px] leading-4 font-semibold md:top-0.5 md:right-0.5',
                    inboxOverdue ? 'bg-accent text-on-accent' : 'bg-ink text-on-ink',
                  )}
                >
                  {inboxCount > 99 ? '99+' : inboxCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => setAi(true)}
              className="flex h-11 items-center gap-2 rounded-md bg-ink px-3.5 text-[14px] font-medium text-on-ink hover:opacity-90 md:h-10"
            >
              <span className="size-2 rounded-full bg-accent" aria-hidden />
              <span className="hidden sm:inline">Спросить Pilot</span>
              <span className="sm:hidden">Pilot</span>
            </button>
            {/* «Сообщить»: ошибка, помощь, доработка с этой страницы (на телефоне — круглая кнопка внизу) */}
            <ReportButton />
            <button
              type="button"
              onClick={() => supportUi.openHelp()}
              className="inline-flex size-11 items-center justify-center rounded-md text-ink-2 hover:bg-tile hover:text-ink md:size-10"
              aria-label="Помощь"
              title="Помощь"
            >
              <CircleHelp className="size-5" />
            </button>
            <UserMenu />
          </header>
          {/* На телефоне строка «где вы находитесь» — отдельной полосой под шапкой */}
          <div className="flex h-11 items-center border-b border-line bg-surface/95 px-2 backdrop-blur md:hidden">
            <LocationBar trail={trail} className="flex w-full" />
          </div>
        </div>
        <main className="min-w-0 flex-1">
          <CrumbsProvider value={setTail}>
            <Outlet />
          </CrumbsProvider>
        </main>
      </div>

      <CommandPalette open={palette} onOpenChange={setPalette} />
      <AiPanel open={ai} onOpenChange={setAi} />
      <HubPanel open={hub} onOpenChange={setHub} />
      <PasskeyOffer />
      <HelpPanel />
      <ReportPanel trail={trail} />
      <SupportDialogs />
      <Tour />
    </div>
  );
}
